import { useEffect, useRef } from "react";

// HlsMobileMenu — full-screen editorial overlay menu for mobile/tablet
// (≤880px). Quiet warm dark surface, sparse type, no chrome. Triggered
// from the homepage nav's "Menu" button. Houses every primary route
// link.
//
// Keyboard:
//   • Escape closes.
//   • Focus is moved to the close button on open.
// Body scroll lock is applied while open so the page beneath stays put.

// Mirrors the desktop primary nav exactly:
//   Work → /work (route)
//   Process → #process-section (in-page anchor)
//   Press → /press (route)
//   Studio → #studio (in-page anchor)
//   Careers → /careers (route)
//   Enquire → /enquire (route)
const LINKS = [
  { label: "Work", kind: "route", to: "/work" },
  { label: "Process", kind: "scroll", target: "process-section" },
  { label: "Press", kind: "route", to: "/press" },
  { label: "Studio", kind: "scroll", target: "studio" },
  { label: "Careers", kind: "route", to: "/careers" },
  { label: "Enquire", kind: "route", to: "/enquire" },
];

export default function HlsMobileMenu({ open, onClose, onScrollTo, onNavigate }) {
  const closeBtnRef = useRef(null);
  const panelRef = useRef(null);

  // Esc to close + focus management + body scroll lock.
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    // Move focus to close button on next paint (avoids fighting iOS focus race).
    const id = requestAnimationFrame(() => {
      closeBtnRef.current?.focus();
    });

    // Body scroll lock — store and restore.
    const prevOverflow = document.body.style.overflow;
    const prevPad = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    document.body.dataset.mobileMenuOpen = "true";

    return () => {
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(id);
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPad;
      delete document.body.dataset.mobileMenuOpen;
    };
  }, [open, onClose]);

  const handleClick = (link) => (e) => {
    if (link.kind === "external") return; // let the anchor open in new tab
    e.preventDefault();
    onClose();
    // Defer so the overlay's exit transition can begin before the page scrolls.
    setTimeout(() => {
      if (link.kind === "scroll") onScrollTo(link.target);
      else if (link.kind === "route") onNavigate(link.to);
    }, 60);
  };

  return (
    <div
      className={["hls-mm", open ? "is-open" : ""].join(" ")}
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
      aria-hidden={!open}
      data-testid="hls-mobile-menu"
      data-state={open ? "open" : "closed"}
    >
      <div className="hls-mm__scrim" aria-hidden="true" onClick={onClose} />

      <div className="hls-mm__panel" ref={panelRef}>
        <div className="hls-mm__row">
          <span className="hls-mm__brand brand-wordmark">Ōraṇi</span>
          <button
            ref={closeBtnRef}
            type="button"
            className="hls-mm__close"
            onClick={onClose}
            aria-label="Close menu"
            data-testid="hls-mobile-menu-close"
          >
            Close
          </button>
        </div>

        <nav aria-label="Mobile primary">
          <ul className="hls-mm__list">
            {LINKS.map((link) => {
              const testid = `hls-mm-${link.label.toLowerCase()}`;
              if (link.kind === "external") {
                return (
                  <li key={link.label} className="hls-mm__item">
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hls-mm__link"
                      onClick={onClose}
                      tabIndex={open ? 0 : -1}
                      data-testid={testid}
                    >
                      {link.label}
                    </a>
                  </li>
                );
              }
              const href =
                link.kind === "route" ? link.to : `#${link.target}`;
              return (
                <li key={link.label} className="hls-mm__item">
                  <a
                    href={href}
                    className="hls-mm__link"
                    onClick={handleClick(link)}
                    tabIndex={open ? 0 : -1}
                    data-testid={testid}
                  >
                    {link.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="hls-mm__rule" aria-hidden="true" />
      </div>
    </div>
  );
}
